import Route from '@ember/routing/route';
import { service } from '@ember/service';

// Periodic refresh interval (60 seconds) to catch any missed WebSocket updates
const REFRESH_INTERVAL_MS = 60000;

export default class EventRoute extends Route {
  @service eventData;
  @service websocket;
  @service cloudAjax;

  // Timer for periodic data refresh
  _refreshTimer = null;
  _currentEventUuid = null;

  model(params) {
    const eventUuid = params.event_uuid;

    console.log(`[EventRoute] Loading event ${eventUuid}`);

    // Initialize event data structure immediately (so template can render loading state)
    if (!this.eventData.events.has(eventUuid)) {
      this.eventData.events.set(eventUuid, this.eventData.createEventDataStructure(eventUuid));
    }

    // Load data asynchronously - don't block route transition
    this.loadEventDataAsync(eventUuid);

    // Capture service references for use in getters
    const eventDataService = this.eventData;

    // Return immediately so route transitions right away
    return {
      eventUuid,
      get classGroups() {
        return eventDataService.getClassGroups(eventUuid);
      },
      get eventDataObj() {
        return eventDataService.getEventData(eventUuid);
      }
    };
  }

  async loadEventDataAsync(eventUuid) {
    this._currentEventUuid = eventUuid;

    // Set up sequence gap callback to trigger refresh when messages are missed
    this.eventData.setSequenceGapCallback((gapEventUuid) => {
      if (gapEventUuid === this._currentEventUuid) {
        console.log(`[EventRoute] Sequence gap detected, triggering immediate refresh`);
        this.refreshTimingData(gapEventUuid);
      }
    });

    // Subscribe to WebSocket for real-time updates
    try {
      console.log(`[EventRoute] Connecting to WebSocket for event ${eventUuid}...`);
      await this.eventData.subscribeToEvent(eventUuid);
      console.log(`[EventRoute] WebSocket connected for event ${eventUuid}`);
    } catch (error) {
      console.error(`[EventRoute] WebSocket connection failed:`, error);
    }

    // Fetch reference and timing data via REST
    try {
      console.log(`[EventRoute] Fetching initial data for event ${eventUuid}...`);
      const [referenceData, timingData] = await Promise.all([
        this.cloudAjax.getEventReference(eventUuid),
        this.cloudAjax.getEventTiming(eventUuid)
      ]);

      // Load data into event-data service
      this.eventData.loadReferenceData(eventUuid, referenceData);
      this.eventData.loadTimingData(eventUuid, timingData);

      // Reset sequence tracking after REST load (new baseline)
      this.eventData.resetSequenceTracking(eventUuid);

      console.log(`[EventRoute] Initial data loaded for event ${eventUuid}`);

      // Start periodic refresh timer as safety net for missed WebSocket updates
      this.startPeriodicRefresh(eventUuid);
    } catch (error) {
      console.error(`[EventRoute] Error fetching initial data:`, error);
    }
  }

  /**
   * Start periodic refresh timer to fetch timing data every 60 seconds.
   * This acts as a safety net to catch any WebSocket updates that may have
   * been missed due to connection issues or race conditions.
   */
  startPeriodicRefresh(eventUuid) {
    // Clear any existing timer
    this.stopPeriodicRefresh();

    console.log(`[EventRoute] Starting periodic refresh (every ${REFRESH_INTERVAL_MS / 1000}s)`);

    this._refreshTimer = setInterval(async () => {
      await this.refreshTimingData(eventUuid);
    }, REFRESH_INTERVAL_MS);
  }

  /**
   * Stop the periodic refresh timer
   */
  stopPeriodicRefresh() {
    if (this._refreshTimer) {
      clearInterval(this._refreshTimer);
      this._refreshTimer = null;
      console.log(`[EventRoute] Stopped periodic refresh`);
    }
  }

  /**
   * Fetch fresh timing data and merge with existing data.
   * This is a safety net refresh that doesn't overwrite fresher WebSocket data.
   */
  async refreshTimingData(eventUuid) {
    try {
      console.log(`[EventRoute] Periodic refresh: fetching timing data for event ${eventUuid}`);
      const timingData = await this.cloudAjax.getEventTiming(eventUuid);
      // loadTimingData now uses merge logic and won't overwrite fresher WebSocket data
      this.eventData.loadTimingData(eventUuid, timingData);
      // Clear the sequence tracker's refetch-in-flight flag so a subsequent
      // gap can re-trigger a refresh.
      this.eventData.resetSequenceTracking(eventUuid);
      console.log(`[EventRoute] Periodic refresh: timing data merged`);
    } catch (error) {
      console.error(`[EventRoute] Periodic refresh failed:`, error);
      // Still clear the flag so the next gap gets a chance to trigger a retry
      // rather than being suppressed forever by a single failed refetch.
      this.eventData.resetSequenceTracking(eventUuid);
    }
  }

  /**
   * Clean up when leaving the route
   */
  deactivate() {
    super.deactivate(...arguments);
    this.stopPeriodicRefresh();

    // Clean up sequence gap callback
    this.eventData.setSequenceGapCallback(null);
  }
}
